/** Narrower images, e.g. a crop of the transaction, are enlarged so small text is readable. */
const MIN_WIDTH = 1000;
const TARGET_WIDTH_WHEN_SMALL = 1400;
const MAX_UPSCALE = 3;

const PHONE_SCREENSHOT_WIDTH = 800;

const MIN_PHONE_SCREENSHOT_WIDTH = 720;
const MAX_PHONE_SCREENSHOT_WIDTH = 1440;
const MIN_PHONE_SCREENSHOT_ASPECT_RATIO = 1.75;
const MAX_WIDTH = 3000;
const DARK_BACKGROUND_LUMINANCE = 110;

function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('The file could not be opened as an image'));
    };
    image.src = url;
  });
}

function getScale(width: number, height: number): number {
  const isPhoneScreenshot =
    height / width >= MIN_PHONE_SCREENSHOT_ASPECT_RATIO &&
    width >= MIN_PHONE_SCREENSHOT_WIDTH &&
    width <= MAX_PHONE_SCREENSHOT_WIDTH;

  if (isPhoneScreenshot) {
    return width > PHONE_SCREENSHOT_WIDTH ? PHONE_SCREENSHOT_WIDTH / width : 1;
  }

  if (width < MIN_WIDTH) {
    return Math.min(MAX_UPSCALE, TARGET_WIDTH_WHEN_SMALL / width);
  }

  return width > MAX_WIDTH ? MAX_WIDTH / width : 1;
}

/**
 * Converts a screenshot into a grayscale canvas that Tesseract reads more
 * reliably and faster: small crops are upscaled (OCR struggles with text
 * under ~20px), phone screenshots and huge photos are downscaled, and
 * dark-mode screenshots are inverted to dark text on a light background.
 */
export async function prepareImageForOcr(
  file: Blob,
): Promise<HTMLCanvasElement> {
  const image = await loadImage(file);
  const scale = getScale(image.naturalWidth, image.naturalHeight);
  const canvas = document.createElement('canvas');

  canvas.width = Math.round(image.naturalWidth * scale);
  canvas.height = Math.round(image.naturalHeight * scale);

  const context = canvas.getContext('2d', { willReadFrequently: true });

  if (!context) {
    throw new Error('Canvas 2D context is not available');
  }

  context.imageSmoothingQuality = 'high';
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = imageData.data;
  let luminanceSum = 0;

  for (let i = 0; i < pixels.length; i += 4) {
    const luminance =
      (pixels[i] ?? 0) * 0.299 +
      (pixels[i + 1] ?? 0) * 0.587 +
      (pixels[i + 2] ?? 0) * 0.114;

    pixels[i] = pixels[i + 1] = pixels[i + 2] = luminance;
    luminanceSum += luminance;
  }

  if (luminanceSum / (pixels.length / 4) < DARK_BACKGROUND_LUMINANCE) {
    for (let i = 0; i < pixels.length; i += 4) {
      pixels[i] = pixels[i + 1] = pixels[i + 2] = 255 - (pixels[i] ?? 0);
    }
  }

  context.putImageData(imageData, 0, 0);

  return canvas;
}
